import {
  DynamicPageVersion,
  DynamicPageVersionDiff,
} from '@betterfunnel/types';
import Button from '@betterfunnel/ui-core/Button';
import IconButton from '@betterfunnel/ui-core/IconButton';
import Loader from '@betterfunnel/ui-core/Loader';
import TextArea from '@betterfunnel/ui-core/TextArea';
import TextField from '@betterfunnel/ui-core/TextField';
import Tooltip from '@betterfunnel/ui-core/Tooltip';
import { diffDynamicPageVersions } from '@betterfunnel/utils';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ArrowsRightLeftIcon } from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { DashboardContext } from '../contexts/DashboardContext';
import { useSnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import {
  createDynamicPageVersion,
  createPreviewDynamicPageVersion,
  getPrevDynamicPageVersion,
} from '../services/DynamicPagesService';
import { capitalize } from '../utils';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function PublishDynamicPageVersionDrawer({
  open,
  onClose,
}: Props) {
  const { show } = useSnackbarContext();
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useContext(DashboardContext);

  const [prevVersion, setPrevVersion] =
    useState<QueryDocumentSnapshot<DynamicPageVersion> | null>();
  const [newVersion, setNewVersion] = useState<DynamicPageVersion>();
  const [isLoadingPublish, setIsLoadingPublish] = useState(false);

  const [error, setError] = useState<Error>();

  const versionsDiff: DynamicPageVersionDiff[] = useMemo(() => {
    if (!prevVersion || !newVersion) return [];

    return diffDynamicPageVersions(prevVersion.data(), newVersion);
  }, [prevVersion, newVersion]);

  useEffect(() => {
    setPrevVersion(undefined);

    if (!open) return;
    if (!selectedAccount) return;
    if (!selectedDynamicPage) return;

    let relevant = true;
    getPrevDynamicPageVersion(selectedAccount.id, selectedDynamicPage.id).then(
      (versionDocs) => {
        if (!relevant) return;
        setPrevVersion(versionDocs?.[0] || null);
      },
    );

    return () => {
      relevant = false;
    };
  }, [open, selectedAccount, selectedDynamicPage]);

  useEffect(() => {
    setError(undefined);
    setNewVersion(undefined);

    if (!open) return;
    if (!selectedDynamicPage) return;
    if (!selectedAccount) return;

    let relevant = true;
    createPreviewDynamicPageVersion(selectedAccount.id, selectedDynamicPage.id)
      .then((version) => {
        if (!relevant) return;
        setNewVersion(version);
      })
      .catch((e) => {
        if (!relevant) return;
        Sentry.captureException(e);
        console.error('Failed to create new version:', e);
        setError(e);
      });

    return () => {
      relevant = false;
    };
  }, [open, selectedAccount, selectedDynamicPage]);

  const isPublishButtonDusabled = useMemo(() => {
    return !newVersion || !newVersion.name.trim();
  }, [newVersion]);

  const onPublishVersionButtonClick = useCallback(() => {
    if (!selectedDynamicPage || !selectedAccount || !newVersion) {
      show('Failed to publish version', 'warn');
      return;
    }

    if (!newVersion.name.trim()) {
      show('Version name is required', 'warn');
      return;
    }

    setIsLoadingPublish(true);
    createDynamicPageVersion(selectedAccount.id, selectedDynamicPage.id, {
      ...newVersion,
      name: newVersion.name.trim(),
      description: newVersion.description?.trim(),
      changes: JSON.parse(JSON.stringify(versionsDiff)),
    })
      .then(() => {
        show('Version published', 'success');
        onClose();
      })
      .catch((e) => {
        console.error('Failed to publish version:', e);
        Sentry.captureException(e);
        show('Failed to publish version', 'warn');
      })
      .finally(() => {
        setIsLoadingPublish(false);
      });
  }, [
    newVersion,
    onClose,
    selectedAccount,
    selectedDynamicPage,
    show,
    versionsDiff,
  ]);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <div className="fixed inset-0" />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-0 sm:pl-16">
            <DialogPanel
              transition
              className="pointer-events-auto w-screen max-w-2xl transform transition duration-500 ease-in-out data-closed:translate-x-full sm:duration-700"
            >
              <form className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                <div className="flex-1">
                  {/* Header */}
                  <div className="bg-gray-50 px-4 py-6 sm:px-6">
                    <div className="flex items-start justify-between space-x-3">
                      <div className="space-y-1">
                        <DialogTitle className="text-base font-semibold text-gray-900">
                          Create a New Dynamic Page Version
                        </DialogTitle>
                        <p className="text-sm text-gray-500">
                          Publish a new version of the dynamic page to make it
                          live.
                        </p>
                      </div>
                      <div className="flex h-7 items-center">
                        <IconButton variant="ternary" onClick={() => onClose()}>
                          <XMarkIcon aria-hidden="true" className="size-6" />
                        </IconButton>
                      </div>
                    </div>
                  </div>

                  {/* Divider container */}
                  {!newVersion && error && (
                    <div className="text-sm/6 text-red-600">
                      {error.message || 'Failed to create version'}
                    </div>
                  )}

                  <DynamicPageVersionDetailsForm
                    version={newVersion}
                    onChange={setNewVersion}
                    changes={versionsDiff}
                    hasPrevVersion={!!prevVersion}
                  />
                </div>

                {/* Action buttons */}
                <div className="shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6">
                  <div className="flex justify-end space-x-3">
                    <Button variant="secondary" onClick={() => onClose()}>
                      Cancel
                    </Button>
                    <Button
                      loading={isLoadingPublish}
                      variant="primary"
                      disabled={isPublishButtonDusabled}
                      onClick={onPublishVersionButtonClick}
                    >
                      Publish
                    </Button>
                  </div>
                </div>
              </form>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

interface DynamicPageVersionDetailsFormProps {
  version?: DynamicPageVersion;
  onChange?: (version: DynamicPageVersion) => void;
  readOnly?: boolean;
  disabled?: boolean;
  changes?: DynamicPageVersionDiff[];
  hasPrevVersion?: boolean;
}
export const DynamicPageVersionDetailsForm = ({
  version,
  onChange,
  readOnly,
  disabled,
  changes,
  hasPrevVersion,
}: DynamicPageVersionDetailsFormProps) => {
  const { isSupportAdmin } = useContext(AuthContext);
  const versionsDiff = changes || version?.changes || [];

  return (
    <div className="space-y-6 py-6 sm:space-y-0 sm:divide-y sm:divide-gray-200 sm:py-0">
      {/* Version name */}
      <div className="space-y-2 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
        <div>
          <label
            htmlFor="version-name"
            className="block text-sm/6 font-medium text-gray-900 sm:mt-1.5"
          >
            Version Name
          </label>
        </div>
        <div className="sm:col-span-2">
          <TextField
            input={{
              id: 'version-name',
              name: 'version-name',
            }}
            readOnly={readOnly}
            disabled={disabled}
            value={version?.name || ''}
            onChange={(e) => {
              if (!version) return;

              onChange?.({
                ...version,
                name: e.target.value,
              });
            }}
          />
        </div>
      </div>

      {/* Version description */}
      <div className="space-y-2 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
        <div>
          <label
            htmlFor="version-description"
            className="block text-sm/6 font-medium text-gray-900 sm:mt-1.5"
          >
            Description (optional)
          </label>
        </div>
        <div className="sm:col-span-2">
          <TextArea
            id="version-description"
            name="version-description"
            readOnly={readOnly}
            disabled={disabled}
            value={version?.description || ''}
            rows={3}
            onChange={(e) => {
              if (!version) return;

              onChange?.({
                ...version,
                description: e.target.value,
              });
            }}
          />
        </div>
      </div>

      {/* Changes */}

      <div className="space-y-2 px-4 sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
        <legend className="sr-only">Changes</legend>
        <div aria-hidden="true" className="text-sm/6 font-medium text-gray-900">
          Changes
        </div>
        <div className="space-y-5 sm:col-span-2">
          <div className="space-y-5 sm:mt-0">
            {!version && (
              <div className="flex gap-2">
                <Loader />
                <span className="text-sm/6 text-gray-500">
                  Loading changes...
                </span>
              </div>
            )}
            {version && !versionsDiff.length && !hasPrevVersion && (
              <div>This is the first version</div>
            )}
            {version && !versionsDiff.length && hasPrevVersion && (
              <div>No changes</div>
            )}
            {version && !!versionsDiff.length && (
              <ul
                role="list"
                className="divide-y divide-gray-100 rounded-md border border-gray-200"
              >
                {versionsDiff.map((diff) => (
                  <li
                    key={`${diff.id}-${diff.type}-${diff.entity}`}
                    className="flex items-center justify-between py-4 pr-5 pl-4 text-sm/6"
                  >
                    <Tooltip
                      disabled={!isSupportAdmin || diff.type !== 'changed'}
                      wrapperClassName=" w-0 flex-1 "
                      text={
                        diff.type === 'changed'
                          ? JSON.stringify(diff.diff, undefined, 2)
                          : ''
                      }
                    >
                      <div
                        className="flex items-center"
                        onClick={() => {
                          if (!isSupportAdmin) return;
                          if (diff.type !== 'changed') return;
                          // copy diff to clipboard
                          navigator.clipboard.writeText(
                            JSON.stringify(diff.diff, undefined, 2),
                          );
                        }}
                      >
                        <ArrowsRightLeftIcon
                          aria-hidden="true"
                          className="size-5 shrink-0 text-gray-400"
                        />
                        <div className="ml-4 flex min-w-0 flex-1 items-center justify-between gap-6">
                          <span className="font-medium">
                            {diff.explanation}
                          </span>
                          <span className="shrink-0 text-gray-400">
                            {capitalize(diff.type)}
                          </span>
                        </div>
                      </div>
                    </Tooltip>
                    {/* <div className="ml-4 shrink-0">
                                    <a
                                      href="#"
                                      className="font-medium text-indigo-600 hover:text-indigo-500"
                                    >
                                      Download
                                    </a>
                                  </div> */}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
